import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, Link as LinkIcon, FloppyDisk } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSettingsPage() {
  useRequireAdmin();
  const [settings, setSettings] = useState({ mb_way_image: '', mb_image: '', cancellation_policy: '', price_list: '', price_list_image: '', privacy_policy: '', terms_and_conditions: '', withdrawal_waiver_text: '', whatsapp_confirmation_template: '' });
  const [programDescriptions, setProgramDescriptions] = useState({});
  const [nutriLinks, setNutriLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/payment-settings`);
        setSettings({
          mb_way_image: data.mb_way_image || '',
          mb_image: data.mb_image || '',
          cancellation_policy: data.cancellation_policy || '',
          price_list: data.price_list || '',
          price_list_image: data.price_list_image || '',
          privacy_policy: data.privacy_policy || '',
          terms_and_conditions: data.terms_and_conditions || '',
          withdrawal_waiver_text: data.withdrawal_waiver_text || '',
          whatsapp_confirmation_template: data.whatsapp_confirmation_template || '',
        });
        setProgramDescriptions(data.program_descriptions && typeof data.program_descriptions === 'object' ? data.program_descriptions : {});
        setNutriLinks(Array.isArray(data.nutritionist_links) ? data.nutritionist_links : []);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const upload = async (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(s => ({ ...s, [field]: data.path || data.url || '' }));
      toast.success('Imagem carregada');
    } catch { toast.error('Erro a carregar imagem'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const stripe_links = {};
      nutriLinks.forEach(n => { stripe_links[n.user_id] = (n.stripe_link || '').trim(); });
      await axios.put(`${API}/api/payment-settings`, { ...settings, program_descriptions: programDescriptions, stripe_links });
      toast.success('Definições guardadas');
    } catch { toast.error('Erro a guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-5 text-center text-brand-muted">A carregar...</div>;

  const imgUrl = (p) => p ? (p.startsWith('http') ? p : `${API}/api/files/${p}`) : '';

  return (
    <div className="p-5 max-w-3xl mx-auto" data-testid="payment-settings-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl text-brand-text">Definições de Pagamentos</h1>
        <p className="text-xs text-brand-muted">Preçário, MB Way, Multibanco e política aparecem na página <strong>Pagamentos</strong> da paciente. A política também vai no <strong>email de confirmação</strong> da consulta. O <strong>link de pagamento</strong> (por nutricionista) aparece no email de confirmação e nos lembretes de 48h/24h quando a consulta não está paga.</p>
      </div>

      <div className="space-y-5">
        {/* MB Way image */}
        <div className="bg-white border border-brand-border rounded-3xl p-5">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Imagem MB Way</Label>
          <p className="text-[11px] text-brand-muted mb-3">QR code ou indicações de pagamento por MB Way.</p>
          {settings.mb_way_image && <img src={imgUrl(settings.mb_way_image)} alt="MB Way" className="w-32 h-32 object-contain rounded-xl border border-brand-border mb-2" data-testid="mb-way-preview" />}
          <label className="inline-flex items-center gap-2 text-xs bg-brand-bg border border-brand-border rounded-full px-3 py-1.5 cursor-pointer hover:bg-brand-bg/80">
            <UploadSimple size={13} /> Carregar imagem
            <input type="file" accept="image/*" className="hidden" onChange={e => upload('mb_way_image', e.target.files?.[0])} data-testid="mb-way-upload" />
          </label>
        </div>

        {/* MB / Multibanco image */}
        <div className="bg-white border border-brand-border rounded-3xl p-5">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Imagem Multibanco</Label>
          <p className="text-[11px] text-brand-muted mb-3">Referência ou IBAN para pagamentos por transferência.</p>
          {settings.mb_image && <img src={imgUrl(settings.mb_image)} alt="MB" className="w-32 h-32 object-contain rounded-xl border border-brand-border mb-2" data-testid="mb-preview" />}
          <label className="inline-flex items-center gap-2 text-xs bg-brand-bg border border-brand-border rounded-full px-3 py-1.5 cursor-pointer hover:bg-brand-bg/80">
            <UploadSimple size={13} /> Carregar imagem
            <input type="file" accept="image/*" className="hidden" onChange={e => upload('mb_image', e.target.files?.[0])} data-testid="mb-upload" />
          </label>
        </div>

        {/* Stripe link — por nutricionista */}
        <div className="bg-white border border-brand-border rounded-3xl p-5">
          <Label className="text-sm text-brand-text font-medium mb-2 block flex items-center gap-1.5"><LinkIcon size={14} /> Link de pagamento (por nutricionista)</Label>
          <p className="text-[11px] text-brand-muted mb-3">Cada nutricionista tem o seu link. Os emails usam o link da nutricionista da consulta (o link específico da consulta, se existir, tem prioridade).</p>
          <div className="space-y-3">
            {nutriLinks.length === 0 && <p className="text-[11px] text-brand-placeholder">Sem nutricionistas.</p>}
            {nutriLinks.map((n, idx) => (
              <div key={n.user_id} data-testid={`nutri-link-row-${n.user_id}`}>
                <Label className="text-[11px] text-brand-muted mb-1 block">{n.name}</Label>
                <Input type="url" value={n.stripe_link || ''} onChange={e => setNutriLinks(prev => prev.map((x, i) => i === idx ? { ...x, stripe_link: e.target.value } : x))} placeholder="https://buy.stripe.com/..." className="rounded-xl border-brand-border" data-testid={`stripe-link-${n.user_id}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Cancellation policy */}
        <div className="bg-white border border-brand-border rounded-3xl p-5">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Política de cancelamento</Label>
          <Textarea value={settings.cancellation_policy} onChange={e => setSettings({ ...settings, cancellation_policy: e.target.value })} rows={5} placeholder="Ex: Cancelamentos com menos de 24h..." className="rounded-xl border-brand-border" data-testid="cancellation-policy" />
          <p className="text-[11px] text-brand-muted mt-1.5">Aceita <strong>Markdown</strong>: **negrito**, listas com <code>-</code> e quebras de linha. Aparece na página Pagamentos da paciente, na marcação pública e nos emails.</p>
        </div>

        {/* Price list image */}
        <div className="bg-white border border-brand-border rounded-3xl p-5">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Preçário (imagem)</Label>
          <p className="text-[11px] text-brand-muted mb-3">Carrega uma imagem com a tabela de preços. Substitui o texto livre anterior.</p>
          {settings.price_list_image && <img src={imgUrl(settings.price_list_image)} alt="Preçário" className="max-w-full max-h-72 object-contain rounded-xl border border-brand-border mb-2" data-testid="price-list-preview" />}
          <label className="inline-flex items-center gap-2 text-xs bg-brand-bg border border-brand-border rounded-full px-3 py-1.5 cursor-pointer hover:bg-brand-bg/80">
            <UploadSimple size={13} /> Carregar imagem
            <input type="file" accept="image/*" className="hidden" onChange={e => upload('price_list_image', e.target.files?.[0])} data-testid="price-list-upload" />
          </label>
        </div>

        {/* ── Programas e textos legais ── */}
        <div className="pt-2">
          <h2 className="font-heading text-lg text-brand-text flex items-center gap-2">Programas e textos legais</h2>
          <p className="text-[11px] text-brand-muted mb-1">Descrições dos programas (marcação pública e lista de espera) e Política de Privacidade.</p>
        </div>

        {/* Program descriptions */}
        <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="program-descriptions-section">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Descrições dos programas</Label>
          <p className="text-[11px] text-brand-muted mb-3">Texto curto de ajuda apresentado por baixo do seletor "Programa de interesse".</p>
          <div className="space-y-3">
            {Object.keys(programDescriptions).map(prog => (
              <div key={prog} data-testid={`prog-desc-row-${prog}`}>
                <Label className="text-[11px] text-brand-muted mb-1 block">{prog}</Label>
                <Textarea value={programDescriptions[prog] || ''} onChange={e => setProgramDescriptions(pd => ({ ...pd, [prog]: e.target.value }))} rows={2} className="rounded-xl border-brand-border text-sm" data-testid={`prog-desc-${prog}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Privacy policy */}
        <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="privacy-policy-section">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Política de Privacidade (RGPD)</Label>
          <Textarea value={settings.privacy_policy} onChange={e => setSettings({ ...settings, privacy_policy: e.target.value })} rows={8} placeholder="Cola aqui a Política de Privacidade..." className="rounded-xl border-brand-border" data-testid="privacy-policy-input" />
          <p className="text-[11px] text-brand-muted mt-1.5">Aceita <strong>Markdown</strong>. Aparece na página pública <code>/privacidade</code>, ligada pela checkbox obrigatória na marcação e na lista de espera.</p>
        </div>

        {/* Terms and conditions */}
        <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="terms-conditions-section">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Termos e Condições Gerais</Label>
          <Textarea value={settings.terms_and_conditions} onChange={e => setSettings({ ...settings, terms_and_conditions: e.target.value })} rows={12} placeholder="Cola aqui os Termos e Condições Gerais..." className="rounded-xl border-brand-border" data-testid="terms-conditions-input" />
          <p className="text-[11px] text-brand-muted mt-1.5">Aceita <strong>Markdown</strong>. Aparece na página pública <code>/termos-e-condicoes</code>.</p>
        </div>

        {/* Withdrawal right waiver (14 days) */}
        <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="withdrawal-waiver-section">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Renúncia ao direito de livre resolução (14 dias)</Label>
          <Textarea value={settings.withdrawal_waiver_text} onChange={e => setSettings({ ...settings, withdrawal_waiver_text: e.target.value })} rows={5} placeholder="Texto de renúncia ao prazo de 14 dias..." className="rounded-xl border-brand-border" data-testid="withdrawal-waiver-input" />
          <p className="text-[11px] text-brand-muted mt-1.5">Aceite por checkbox obrigatória e separada, só na marcação de <strong>consulta paga</strong> em <code>/agendarconsulta</code> (o serviço tem início antes de decorrer o prazo de 14 dias previsto no DL 24/2014).</p>
        </div>

        {/* WhatsApp confirmation template */}
        <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="whatsapp-template-section">
          <Label className="text-sm text-brand-text font-medium mb-2 block">Mensagem de confirmação por WhatsApp</Label>
          <Textarea value={settings.whatsapp_confirmation_template} onChange={e => setSettings({ ...settings, whatsapp_confirmation_template: e.target.value })} rows={4} placeholder="Olá {nome} 💛 ..." className="rounded-xl border-brand-border" data-testid="whatsapp-template-input" />
          <p className="text-[11px] text-brand-muted mt-1.5">Usada no botão WhatsApp do Painel inicial. Variáveis: <code>{'{nome}'}</code>, <code>{'{dia}'}</code>, <code>{'{hora}'}</code>, <code>{'{formato}'}</code>, <code>{'{link_pagamento}'}</code>. Coloca a frase do pagamento entre <code>[[pagamento]]</code> e <code>[[/pagamento]]</code>, é omitida automaticamente quando a consulta já está paga.</p>
        </div>

        <button onClick={save} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-payment-settings">
          <FloppyDisk size={16} /> {saving ? 'A guardar...' : 'Guardar definições'}
        </button>
      </div>
    </div>
  );
}
